import {
  CompassOutlined,
  DatabaseOutlined,
  GlobalOutlined,
  HistoryOutlined,
  InfoCircleFilled,
  MinusOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Card, Spin, Divider, Select } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import useReportLog from '../../../../hooks/useReportLog';
import styled from 'styled-components';
import {
  convertShortAELabel,
  convertShortFMLabel,
  toHHMMSS,
  uniqueByLabel,
} from '../helperFunction';
import useGroup from '../../../../zustand/useGroup';
import useAppliedReportFilter from '../../../../hooks/useAppliedReportFilter';
import useReportSummaryData from '../../../../hooks/useReportSummaryData';
import { EAssetType } from '../../../asset/types/asset.type';
import {
  cruiseUnitOptions,
  distanceUnitOptions,
  speedUnitOptions,
} from '../../../../helpers/unit';
import { DeviceProperties } from '../../../device/types/device.type';
import { resolveSummaryColumns } from '../tableColumn';

interface IProps {
  defaultOpenBody?: string[];
  hideAccordion?: boolean;
  devices: DeviceProperties[];
  assetType: EAssetType;
}

const ReportSummary: React.FC<IProps> = (props) => {
  const {
    loadingLog: loading,
    logs,
    speedUnit,
    setSpeedUnit,
    distanceUnit,
    setDistanceUnit,
    cruiseUnit,
    setCruiseUnit,
  } = useReportLog();
  const { filter, asset, timezone } = useAppliedReportFilter();
  const [showBody, setShowBody] = useState<{
    body1: boolean;
    body2: boolean;
    body3: boolean;
    body4: boolean;
  }>({
    body1: false,
    body2: false,
    body3: false,
    body4: false,
  });

  function handleShowBody(key: any) {
    const newShowBody: any = { ...showBody };
    newShowBody[key] = !newShowBody[key];
    setShowBody((current) => {
      return {
        ...current,
        ...newShowBody,
      };
    });
  }

  useEffect(() => {
    if (props.defaultOpenBody && props.defaultOpenBody.length) {
      props.defaultOpenBody.forEach((key) => {
        const currentShowBody: any = { ...showBody };
        if (currentShowBody.hasOwnProperty(key)) {
          setShowBody((current) => {
            return {
              ...current,
              [key]: true,
            };
          });
        } else {
          setShowBody((current) => {
            return {
              ...current,
              [key]: false,
            };
          });
        }
      });
    }
  }, [props.defaultOpenBody]);

  const { summary: dataSource, gpsSummary: gpsSummmary } =
    useReportSummaryData({
      startAt: filter.startAt,
      endAt: filter.endAt,
      interval: filter.interval,
      timezone,
    });

  const validateDataSummary = useMemo(() => {
    return resolveSummaryColumns(props.devices);
  }, [logs, props.devices]);

  const _defaultCardInfo = () => {
    return (
      <CardInfo size='small' customGrid='1fr'>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            opacity: 0.5,
          }}
        >
          <InfoCircleFilled style={{ opacity: 0.4 }} />
          <div
            style={{
              fontWeight: 'normal',
              fontSize: 12,
              textAlign: 'center',
              marginTop: 5,
            }}
          >
            No Data
          </div>
        </div>
      </CardInfo>
    );
  };

  const _renderCardFM = (source: any, type: string) => {
    const sourceKeys = Object.keys(source);

    const fmKeys = sourceKeys.filter((k) => {
      if (type === 'fuelCons') {
        if (/^totalEngineFuelCons/i.test(k)) return true;
        return /fm/i.test(k) && /Fuel/i.test(k);
      }
      return false;
    });

    const fmKeysSorted = fmKeys.sort((a, b) => {
      if (type === 'fuelCons') {
        if (a === 'totalEngineFuelCons') return 1;
        if (b === 'totalEngineFuelCons') return -1;
      }

      const parse = (str: string) => {
        const match = str.match(/fm/i)!;
        return {
          fm: Number(match[1]),
          key: match[2],
        };
      };

      const pa = parse(a);
      const pb = parse(b);

      if (pa.fm !== pb.fm) {
        return pa.fm - pb.fm;
      }

      return pa.key.localeCompare(pb.key);
    });

    const keyMapped = fmKeysSorted.map((k) => ({
      key: k,
      label: convertShortFMLabel(k),
    }));

    const uniqueCards = uniqueByLabel(keyMapped);
    console.log('uniqueCards', {source, uniqueCards})
    return uniqueCards.map((item) => (
      <CardInfo size='small' key={item.label}>
        <div>{item.label}</div>
          <div>
            {(source[item.key] || 0).toFixed(2)} <Unit>Liter</Unit>
          </div>
      </CardInfo>
    ));
  };

  const _renderCardAE = (source: any, type: 'fuelCons' | 'runningTime') => {
    const sourceKeys = Object.keys(source);

    const aeKeys = sourceKeys.filter((k) => {
      if (type === 'fuelCons') {
        if (/^totalAEFuelCons/i.test(k)) return true;
        return /ae/i.test(k) && /Fuel/i.test(k);
      }
      if (type === 'runningTime') {
        if (/^totalAERunningTime/i.test(k)) return true;
        return /ae/i.test(k) && /RunningHour|RunningTime/i.test(k);
      }
      return false;
    });

    const aeKeysSorted = aeKeys.sort((a, b) => {
      if (type === 'fuelCons') {
        if (a === 'totalAEFuelCons') return 1;
        if (b === 'totalAEFuelCons') return -1;
      }
      if (type === 'runningTime') {
        if (a === 'totalAERunningTime') return 1;
        if (b === 'totalAERunningTime') return -1;
      }

      const parse = (str: string) => {
        const match = str.match(/ae(\d+)(.*)/i)!;
        return {
          ae: Number(match[1]),
          key: match[2],
        };
      };

      const pa = parse(a);
      const pb = parse(b);

      if (pa.ae !== pb.ae) {
        return pa.ae - pb.ae;
      }

      return pa.key.localeCompare(pb.key);
    });

    const keyMapped = aeKeysSorted.map((k) => ({
      key: k,
      label: convertShortAELabel(k),
    }));

    const uniqueCards = uniqueByLabel(keyMapped);

    return uniqueCards.map((item) => (
      <CardInfo size='small' key={item.label}>
        <div>{item.label}</div>
        {type === 'fuelCons' ? (
          <div>
            {(source[item.key] || 0).toFixed(2)} <Unit>Liter</Unit>
          </div>
        ) : (
          <div>{toHHMMSS(source[item.key] || 0)}</div>
        )}
      </CardInfo>
    ));
  };

  return (
    <React.Fragment>
      <Spin spinning={loading}>
        <CardInfoContainer
          onClick={() => handleShowBody('body1')}
          style={{ cursor: 'pointer' }}
        >
          <CardInfoTitle style={{ marginTop: 15 }}>
            <CompassOutlined style={{ fontSize: 12, marginRight: 5 }} />
            Summary
          </CardInfoTitle>
          {!props.hideAccordion && (
            <Accordion>
              {showBody.body1 ? <MinusOutlined /> : <PlusOutlined />}
            </Accordion>
          )}
        </CardInfoContainer>
        {showBody.body1 && (
          <CardInfoContainer2>
            <CardInfoWrapper>
              <CardInfo size='small'>
                <div>Total Fuel Cons.</div>
                <div>
                  {(
                    dataSource.totalEngineFuelCons + dataSource.totalAEFuelCons
                  ).toFixed(2)}{' '}
                  <Unit>Liter</Unit>
                </div>
              </CardInfo>
            </CardInfoWrapper>
            <CardInfoWrapper>
              <CardInfoUnit size='small'>
                <div style={{ opacity: 0.7 }}>Total Cruise</div>
                <div>
                  {gpsSummmary.totalCruise}
                  <Select
                    size='small'
                    value={cruiseUnit}
                    style={{ width: 80 }}
                    onChange={(e) => setCruiseUnit(e)}
                    options={cruiseUnitOptions}
                  />
                </div>
              </CardInfoUnit>
            </CardInfoWrapper>
            <CardInfoWrapper>
              <CardInfoUnit size='small'>
                <div style={{ opacity: 0.7 }}>Average Speed</div>
                <div>
                  {gpsSummmary.avgSpeed}{' '}
                  <Select
                    size='small'
                    value={speedUnit}
                    style={{ width: 80 }}
                    onChange={(e) => setSpeedUnit(e)}
                    options={speedUnitOptions}
                  />
                </div>
              </CardInfoUnit>
            </CardInfoWrapper>
            <CardInfoWrapper>
              <CardInfoUnit size='small'>
                <div style={{ opacity: 0.7 }}>Distance</div>
                <div>
                  {gpsSummmary.totalDistance}
                  <Select
                    size='small'
                    value={distanceUnit}
                    style={{ width: 80 }}
                    onChange={(e) => setDistanceUnit(e)}
                    options={distanceUnitOptions}
                  />
                </div>
              </CardInfoUnit>
            </CardInfoWrapper>
          </CardInfoContainer2>
        )}

        <Divider style={{ marginBottom: 0, marginTop: 5 }} />

        {/* {![EAssetType.EMS].includes(asset.assetType) && (
          <React.Fragment>
            <CardInfoContainer
              style={{ cursor: 'pointer' }}
              onClick={() => handleShowBody('body2')}
            >
              <CardInfoTitle>
                <DatabaseOutlined style={{ fontSize: 12, marginRight: 5 }} />{' '}
                Total Fuel Consumption
              </CardInfoTitle>
              {!props.hideAccordion && (
                <Accordion>
                  {showBody.body2 ? <MinusOutlined /> : <PlusOutlined />}
                </Accordion>
              )}
            </CardInfoContainer>
            {showBody.body2 && (
              <CardInfoContainer2>
                <CardInfoWrapper>
                  <CardInfo size='small'>
                    <div>Engine Fuel Cons.</div>
                    <div>
                      {dataSource.totalEngineFuelCons.toFixed(2)}{' '}
                      <Unit>Liter</Unit>
                    </div>
                  </CardInfo>
                </CardInfoWrapper>
                <CardInfoWrapper>
                  <CardInfo size='small'>
                    <div>AE Fuel Cons.</div>
                    <div>
                      {dataSource.totalAEFuelCons.toFixed(2)} <Unit>Liter</Unit>
                    </div>
                  </CardInfo>
                </CardInfoWrapper>
                <CardInfoWrapper>
                  <CardInfo size='small'>
                    <div>Total Fuel Cons.</div>
                    <div>
                      {(
                        dataSource.totalEngineFuelCons +
                        dataSource.totalAEFuelCons
                      ).toFixed(2)}{' '}
                      <Unit>Liter</Unit>
                    </div>
                  </CardInfo>
                </CardInfoWrapper>
              </CardInfoContainer2>
            )}
            <Divider style={{ marginBottom: 0, marginTop: 5 }} />
          </React.Fragment>
        )} */}

        <CardInfoContainer2
          style={{ cursor: 'pointer' }}
          onClick={() => handleShowBody('body3')}
        >
          <CardInfoTitle>
            <DatabaseOutlined style={{ fontSize: 12, marginRight: 5 }} />
            Engine Fuel Consumption
          </CardInfoTitle>
          <CardInfoTitle>
            <HistoryOutlined style={{ fontSize: 12, marginRight: 5 }} />
            Engine Running Time
          </CardInfoTitle>
          <CardInfoTitle>
            <GlobalOutlined style={{ fontSize: 12, marginRight: 5 }} />
            Engine Average RPM
          </CardInfoTitle>
          <CardInfoTitle>
            <DatabaseOutlined style={{ fontSize: 12, marginRight: 5 }} />
            Average Flow
          </CardInfoTitle>
          {!props.hideAccordion && (
            <Accordion2>
              {showBody.body3 ? <MinusOutlined /> : <PlusOutlined />}
            </Accordion2>
          )}
        </CardInfoContainer2>
        {showBody.body3 && (
          <CardInfoContainer2>
            <CardInfoWrapper>
              {_renderCardFM(dataSource, 'fuelCons')}
            </CardInfoWrapper>
            {/* <CardInfoWrapper>
              {validateDataSummary.columnFlowmeterPort && (
                <CardInfo size='small'>
                  <div>Port</div>
                  <div>
                    {dataSource.portTotalFuel.toFixed(2)} <Unit>Liter</Unit>
                  </div>
                </CardInfo>
              )}

              {validateDataSummary.columnFlowmeterStarboard && (
                <CardInfo size='small'>
                  <div>Starboard</div>
                  <div>
                    {dataSource.starboardTotalFuel.toFixed(2)}{' '}
                    <Unit>Liter</Unit>
                  </div>
                </CardInfo>
              )}

              {validateDataSummary.columnFlowmeterCenter && (
                <CardInfo size='small'>
                  <div>Center</div>
                  <div>
                    {dataSource.centerTotalFuel.toFixed(2)} <Unit>Liter</Unit>
                  </div>
                </CardInfo>
              )}
              <CardInfo size='small'>
                <div>Total Fuel Cons.</div>
                <div>
                  {dataSource.totalEngineFuelCons.toFixed(2)} <Unit>Liter</Unit>
                </div>
              </CardInfo>
            </CardInfoWrapper> */}

            <CardInfoWrapper>
              {validateDataSummary.columnRPMPort && (
                <CardInfo size='small'>
                  <div>Port</div>
                  <div>{toHHMMSS(dataSource.portRunningHour)}</div>
                </CardInfo>
              )}

              {validateDataSummary.columnRPMStarboard && (
                <CardInfo size='small'>
                  <div>Starboard</div>
                  <div>{toHHMMSS(dataSource.starboardRunningHour)}</div>
                </CardInfo>
              )}

              {validateDataSummary.columnRPMCenter && (
                <CardInfo size='small'>
                  <div>Center</div>
                  <div>{toHHMMSS(dataSource.centerRunningHour)}</div>
                </CardInfo>
              )}

              {!validateDataSummary.columnRPMPort &&
                !validateDataSummary.columnRPMStarboard &&
                !validateDataSummary.columnRPMCenter &&
                _defaultCardInfo()}
            </CardInfoWrapper>
            <CardInfoWrapper>
              {validateDataSummary.columnRPMPort && (
                <CardInfo size='small'>
                  <div>Port</div>
                  <div>
                    {dataSource.portRPM.toFixed(2)} <Unit>RPM</Unit>
                  </div>
                </CardInfo>
              )}

              {validateDataSummary.columnRPMStarboard && (
                <CardInfo size='small'>
                  <div>Starboard</div>
                  <div>
                    {dataSource.starboardRPM.toFixed(2)} <Unit>RPM</Unit>
                  </div>
                </CardInfo>
              )}

              {validateDataSummary.columnRPMCenter && (
                <CardInfo size='small'>
                  <div>Center</div>
                  <div>
                    {dataSource.centerRPM.toFixed(2)} <Unit>RPM</Unit>
                  </div>
                </CardInfo>
              )}

              {!validateDataSummary.columnRPMPort &&
                !validateDataSummary.columnRPMStarboard &&
                !validateDataSummary.columnRPMCenter &&
                _defaultCardInfo()}
            </CardInfoWrapper>
            <CardInfoWrapper>
              {validateDataSummary.columnFlowmeterPort && (
                <CardInfo size='small'>
                  <div>Port</div>
                  <div>
                    {dataSource.portAverageFlow.toFixed(2)}{' '}
                    <Unit>Liter/H</Unit>
                  </div>
                </CardInfo>
              )}

              {validateDataSummary.columnFlowmeterStarboard && (
                <CardInfo size='small'>
                  <div>Starboard</div>
                  <div>
                    {dataSource.starboardAverageFlow.toFixed(2)}{' '}
                    <Unit>Liter/H</Unit>
                  </div>
                </CardInfo>
              )}

              {validateDataSummary.columnFlowmeterCenter && (
                <CardInfo size='small'>
                  <div>Center</div>
                  <div>
                    {dataSource.centerAverageFlow.toFixed(2)}{' '}
                    <Unit>Liter/H</Unit>
                  </div>
                </CardInfo>
              )}

              {!validateDataSummary.columnFlowmeterPort &&
                !validateDataSummary.columnFlowmeterStarboard &&
                !validateDataSummary.columnFlowmeterCenter &&
                _defaultCardInfo()}
            </CardInfoWrapper>
          </CardInfoContainer2>
        )}

        <Divider style={{ marginBottom: 0, marginTop: 5 }} />

        <CardInfoContainer2
          style={{ cursor: 'pointer' }}
          onClick={() => handleShowBody('body4')}
        >
          <CardInfoTitle>
            <DatabaseOutlined style={{ fontSize: 12, marginRight: 5 }} />
            AE Fuel Consumption
          </CardInfoTitle>
          <CardInfoTitle>
            <HistoryOutlined style={{ fontSize: 12, marginRight: 5 }} />
            AE Running Time
          </CardInfoTitle>
          {!props.hideAccordion && (
            <Accordion2>
              {showBody.body4 ? <MinusOutlined /> : <PlusOutlined />}
            </Accordion2>
          )}
        </CardInfoContainer2>
        {showBody.body4 && (
          <CardInfoContainer2>
            <CardInfoWrapper>
              {_renderCardAE(dataSource, 'fuelCons')}
            </CardInfoWrapper>
            <CardInfoWrapper>
              {_renderCardAE(dataSource, 'runningTime')}
              {/* <CardInfo size='small'>
                <div>AE 1</div>
                <div>{toHHMMSS(dataSource.ae1RunningHour)}</div>
              </CardInfo>
              <CardInfo size='small'>
                <div>AE 2</div>
                <div>{toHHMMSS(dataSource.ae2RunningHour)}</div>
              </CardInfo>
              <CardInfo size='small'>
                <div>AE 3</div>
                <div>{toHHMMSS(dataSource.ae3RunningHour)}</div>
              </CardInfo> */}
            </CardInfoWrapper>
          </CardInfoContainer2>
        )}
      </Spin>
    </React.Fragment>
  );
};

const Unit = styled.span`
  opacity: 0.7;
  font-weight: normal !important;
  font-size: 14px;
`;

const CardInfo = styled(Card)<{ customGrid?: string }>`
  background: var(--gray-0);
  .ant-card-body {
    display: grid;
    grid-template-columns: ${(props) => props.customGrid || '1fr 1fr'};
    padding: 5px 15px;

    div {
      :first-child {
        opacity: 0.7;
      }
      :last-child {
        font-size: 16px;
        font-weight: bold;
      }
      span {
        color: black !important;
        opacity: 1 !important;
      }
    }
  }
  .ant-card-body::before,
  .ant-card-body::after {
    display: none;
  }
`;

const CardInfoUnit = styled(Card)`
  background: var(--gray-0);
  .ant-card-body {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding: 5px 15px;
    align-items: center;

    div {
      :last-child {
        font-size: 16px;
        font-weight: bold;
        display: flex;
        align-items: center;
        gap: 10px;
      }
    }
  }
  .ant-card-body::before,
  .ant-card-body::after {
    display: none;
  }
`;

const Accordion = styled.div`
  margin-top: 15px;
  grid-column: 4;
`;

const Accordion2 = styled.div`
  margin-top: 15px;
  grid-column: 5;
`;

const CardInfoTitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  margin-top: 10px;
`;

const CardInfoContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr) 40px;
  gap: 10px;
`;

const CardInfoContainer2 = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr) 40px;
  gap: 10px;
`;

const CardInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export default ReportSummary;
